import React, { useState, useRef, useMemo, useEffect } from 'react';
import { supabase } from '../supabaseClient';
import ReactQuill from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';
import '../lib/quill-theme.css';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import '../lib/datepicker-theme.css';
import MoneyInput from './MoneyInput';
import AutoSizeTextarea from './AutoSizeTextarea';
import { fmtDate, fmtDateTime } from '../lib/dates';
import ConfirmDialog from './ConfirmDialog';
import { sendEmail, briefUpdatedEmail } from '../lib/email';
import { diffBriefVersions, hasBriefChanges, humaniseBriefType, humaniseBool } from '../lib/briefDiff';
import { localTimezoneAbbr } from '../lib/dates';
import { formatGBP as gbp } from '../lib/money';
import { htmlToPlainText } from '../lib/htmlText';

// In-place "Amend Brief" editor, toggled inline on the brief detail (like
// CompleteBriefPanel). Edits the amendable content subset, highlights changes
// live, summarises them, shows who'll be notified, and commits via one of two
// paths:
//   Send Update  — version snapshot + email recipients (respecting mute) +
//                  reopen submitted responses + re-arm the reminder if the
//                  deadline moved.
//   Save Silently — version snapshot only (silent), no email / no reopen /
//                  no supplier indicators.
// Cancel discards with no version written.

const MAX_ATTACHMENT_BYTES = 10 * 1024 * 1024;
const ALLOWED_EXTS = ['pdf', 'doc', 'docx', 'xls', 'xlsx', 'jpg', 'jpeg', 'png', 'gif', 'webp', 'heic', 'heif'];

const fmtMoney = (n) => gbp(n);
// Date helpers now live in src/lib/dates.js — fmtDate / fmtDateTime
// imported above. Local slash-form helpers were removed during the
// brand-wide date format unification (DD Mon YYYY everywhere).
// react-datepicker round-trip: stored ISO/datetime string → Date object →
// "YYYY-MM-DDTHH:mm" / "YYYY-MM-DD" back to state. Matches AddBrief's
// toDate / fromDate so the picker behaves identically on both pages.
const toDateObj = (s) => {
  if (!s) return null;
  const d = new Date(s);
  return isNaN(d.getTime()) ? null : d;
};
const fromDateTime = (d) => {
  if (!d) return '';
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};
const fromDateOnly = (d) => {
  if (!d) return '';
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};
const blankNull = (v) => (v == null || String(v).trim() === '' ? null : v);

export default function AmendBriefPanel({ brief, recipients = [], briefAttachments = [], currentInternalUser, onClose, onDone, onFlightFrom }) {
  // Ref on the Send Update button so the parent's paper-plane flight can
  // launch from where the user actually clicked.
  const sendUpdateBtnRef = useRef(null);
  // Original snapshot captured once — drives the live diff + the v1
  // backfill. Mirrors the full AddBrief amendable surface so the amend
  // panel can edit every field the creation flow does (project number,
  // category, NDA, delivery address, rig + de-rig). The shape MUST stay
  // in lockstep with FIELD_DEFS in src/lib/briefDiff.js — adding a field
  // to one and not the other surfaces phantom diffs.
  const original = useRef({
    brief_name: brief.brief_name || '',
    fusion_project_number: brief.fusion_project_number || '',
    fusion_project_name: brief.fusion_project_name || '',
    brief_category: brief.brief_category || '',
    client: brief.client || '',
    required_response_time: brief.required_response_time || '',
    expected_required_by_date: brief.expected_required_by_date || '',
    delivery_access_to_date: brief.delivery_access_to_date || '',
    event_live_date: brief.event_live_date || '',
    event_live_end_date: brief.event_live_end_date || '',
    location: brief.location || '',
    // Visibility flags. The amend panel edits these fields, so it has to be
    // able to SHOW them: AddBrief owns the toggles but is the draft editor
    // only, so once a brief is sent this was the only editor — and it had no
    // toggles. Adding a budget to a sent brief left it permanently hidden from
    // suppliers with no way to reveal it.
    show_client: brief.show_client ?? false,
    show_event_date: brief.show_event_date ?? false,
    show_delivery_date: brief.show_delivery_date ?? false,
    show_budget: brief.show_budget ?? false,
    show_location: brief.show_location ?? false,
    show_project_name: brief.show_project_name ?? false,
    show_project_number: brief.show_project_number ?? false,
    show_category: brief.show_category ?? false,
    show_delivery_address: brief.show_delivery_address ?? false,
    show_rig: brief.show_rig ?? false,
    show_derig: brief.show_derig ?? false,
    minimum_budget: brief.minimum_budget ?? '',
    maximum_budget: brief.maximum_budget ?? '',
    brief_content: brief.brief_content || '',
    delivery_address: brief.delivery_address || '',
    // delivery_date_na is a UI-only inverse-of-required flag. AddBrief
    // uses it to gate the Delivery Date + Delivery Address fields. NOT
    // a diff field — clearing the date column on save is what the
    // supplier sees.
    delivery_date_na: !!brief.delivery_date_na,
    nda_required: !!brief.nda_required,
    rig_required: !!brief.rig_required,
    rig_start_date: brief.rig_start_date || '',
    rig_end_date: brief.rig_end_date || '',
    rig_details: brief.rig_details || '',
    derig_required: !!brief.derig_required,
    derig_start_date: brief.derig_start_date || '',
    derig_end_date: brief.derig_end_date || '',
    derig_details: brief.derig_details || '',
    // brief_type comes in as null / 'exclusive' / 'competitive'. Kept null
    // (not '') so the diff layer's enum comparison treats unflagged as a
    // first-class state rather than confusing it with an empty string.
    brief_type: brief.brief_type ?? null,
    // Auto-reminder opt-in. Originally only settable at brief-creation
    // time on AddBrief — exposing it here closes the post-send gap that
    // meant the toggle was effectively frozen as soon as the brief left
    // the draft state. Not part of the diff (it's an internal scheduling
    // flag, not supplier-facing content), so it skips FIELD_DEFS.
    auto_reminder_24h: !!brief.auto_reminder_24h,
    document_refs: (briefAttachments || []).map(a => ({ file_url: a.file_url, file_name: a.file_name, display_name: a.display_name || null })),
  }).current;

  // Categories list for the Brief Category dropdown — same source the
  // AddBrief / Briefs filter pull from (list_values table, scoped to
  // the supplier_category list_name).
  const [categories, setCategories] = useState([]);
  useEffect(() => {
    (async () => {
      const { data } = await supabase.from('list_values').select('value').eq('list_name', 'supplier_category').order('display_order');
      setCategories(data || []);
    })();
  }, []);

  const [form, setForm] = useState({ ...original });

  // Date-order validation — extended from the single Event pair to
  // also cover Rig + De-rig (the panel now amends the full surface
  // including rig schedule, so the same end-before-start guard the
  // creation form enforces needs to apply here). Returns the FIRST
  // offending pair's message so the save guard can surface it; the
  // per-pair flags drive inline red copy beneath each end picker.
  const dateOrderErrors = useMemo(() => {
    const out = {};
    // allowEquals=true for Event Live (date-only, single-day events are
    // legitimate). Rig + De-rig carry HH:mm so end===start is a
    // zero-length window — reject as nonsense.
    const check = (startKey, endKey, label, allowEquals) => {
      if (!form[startKey] || !form[endKey]) return;
      const s = new Date(form[startKey]);
      const e = new Date(form[endKey]);
      if (isNaN(s) || isNaN(e)) return;
      if (allowEquals ? e < s : e <= s) {
        out[endKey] = allowEquals
          ? `${label} end must be on or after ${label} start.`
          : `${label} end must be after ${label} start.`;
      }
    };
    check('event_live_date', 'event_live_end_date', 'Event Live', true);
    if (form.rig_required) check('rig_start_date', 'rig_end_date', 'Install', false);
    if (form.derig_required) check('derig_start_date', 'derig_end_date', 'De-rig', false);
    // Cross-stage chronology — rig must finish before / at the event
    // end, de-rig must start at or after the event begins. Catches the
    // "de-rig 07 Jun on a 12-13 Jun event" inversion that prompted the
    // validation rule. Compared on calendar-day floor so same-day rig
    // and event are permitted.
    const dayFloor = (iso) => {
      if (!iso) return null;
      const d = new Date(iso);
      if (isNaN(d)) return null;
      return new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
    };
    const eventStart = dayFloor(form.event_live_date);
    const eventEnd = dayFloor(form.event_live_end_date) ?? eventStart;
    if (eventStart != null && form.rig_required && form.rig_start_date) {
      const rigStart = new Date(form.rig_start_date).getTime();
      if (eventEnd != null && rigStart > eventEnd) {
        out.rig_start_date = 'Install must start on or before the event ends.';
      }
    }
    if (eventStart != null && form.derig_required && form.derig_start_date) {
      const derigStart = new Date(form.derig_start_date).getTime();
      if (derigStart < eventStart) {
        out.derig_start_date = 'De-rig must start on or after the event begins.';
      }
    }
    return out;
  // `form` covers every key the check reads; exhaustive-deps prefers
  // the object reference over per-key listings when the helper is
  // closure-bound to `form[startKey]`. The memo still only re-runs
  // when form actually changes (React's referential equality).
  }, [form]);
  const dateOrderError = Object.values(dateOrderErrors)[0] || null;
  const [attachments, setAttachments] = useState(() => (briefAttachments || []).map(a => ({
    file_url: a.file_url, file_name: a.file_name, display_name: a.display_name || null, file_size_bytes: a.file_size_bytes, mime_type: a.mime_type,
  })));
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState(null);
  const [confirmSend, setConfirmSend] = useState(false);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  // Live diff: original vs current (reuses the brief diff used everywhere).
  const currentSnap = useMemo(() => ({ ...form, document_refs: attachments.map(a => ({ file_url: a.file_url, file_name: a.file_name, display_name: a.display_name || null })) }), [form, attachments]);
  const diff = useMemo(() => diffBriefVersions(original, currentSnap), [original, currentSnap]);
  const changed = hasBriefChanges(diff);
  const changedField = (label) => diff && !diff.isOriginal && diff.fields.some(f => f.field === label);

  // Recipient impact buckets (from the loaded recipients).
  const impact = useMemo(() => {
    const c = { submitted: 0, in_progress: 0, awaiting: 0, declined: 0 };
    for (const r of recipients) {
      if (r.status === 'responded') c.submitted++;
      else if (r.status === 'accepted') c.in_progress++;
      else if (r.status === 'declined') c.declined++;
      else if (r.status === 'pending') c.awaiting++;
    }
    return c;
  }, [recipients]);
  const notifyCount = impact.submitted + impact.in_progress + impact.awaiting;

  const handleFiles = async (fileList) => {
    const files = Array.from(fileList || []);
    if (files.length === 0) return;
    setUploading(true);
    setError(null);
    const added = [];
    for (const file of files) {
      const ext = (file.name.split('.').pop() || '').toLowerCase();
      if (!ALLOWED_EXTS.includes(ext)) { setError(`${file.name}: unsupported type`); continue; }
      if (file.size > MAX_ATTACHMENT_BYTES) { setError(`${file.name}: too large (max 10 MB)`); continue; }
      const safe = file.name.replace(/[^a-zA-Z0-9._-]/g, '_');
      const path = `${brief.id}/amend/${Date.now()}-${safe}`;
      const { error: upErr } = await supabase.storage.from('brief-documents').upload(path, file, { contentType: file.type, upsert: false });
      if (upErr) { setError(`${file.name}: upload failed`); continue; }
      const { data: pub } = supabase.storage.from('brief-documents').getPublicUrl(path);
      added.push({ file_url: pub?.publicUrl || '', file_name: file.name, display_name: null, file_size_bytes: file.size, mime_type: file.type || null });
    }
    if (added.length) setAttachments(prev => [...prev, ...added]);
    setUploading(false);
  };

  const buildSnapshot = (versionNumber, silent, src) => ({
    brief_id: brief.id,
    version_number: versionNumber,
    silent,
    note_to_suppliers: silent ? null : (note.trim() || null),
    created_by: currentInternalUser?.id || null,
    brief_name: src.brief_name || null,
    fusion_project_number: src.fusion_project_number || null,
    fusion_project_name: src.fusion_project_name || null,
    brief_category: src.brief_category || null,
    client: src.client || null,
    required_response_time: blankNull(src.required_response_time),
    // delivery_date_na inverts to "delivery required"; when the
    // toggle is off the date + address columns get blanked so the
    // version snapshot stays coherent with what the supplier sees.
    expected_required_by_date: src.delivery_date_na ? null : blankNull(src.expected_required_by_date),
    delivery_access_to_date: src.delivery_date_na ? null : blankNull(src.delivery_access_to_date),
    delivery_address: src.delivery_date_na ? null : (src.delivery_address || null),
    event_live_date: blankNull(src.event_live_date),
    event_live_end_date: blankNull(src.event_live_end_date),
    location: src.location || null,
    minimum_budget: blankNull(src.minimum_budget),
    maximum_budget: blankNull(src.maximum_budget),
    brief_content: src.brief_content || null,
    brief_type: src.brief_type ?? null,
    nda_required: !!src.nda_required,
    // Rig / de-rig — the required toggle gates everything else; when
    // off, dates + details are cleared on the snapshot too so the
    // historical record matches the live row.
    rig_required: !!src.rig_required,
    rig_start_date: src.rig_required ? blankNull(src.rig_start_date) : null,
    rig_end_date:   src.rig_required ? blankNull(src.rig_end_date)   : null,
    rig_details:    src.rig_required ? (src.rig_details || null)     : null,
    derig_required: !!src.derig_required,
    derig_start_date: src.derig_required ? blankNull(src.derig_start_date) : null,
    derig_end_date:   src.derig_required ? blankNull(src.derig_end_date)   : null,
    derig_details:    src.derig_required ? (src.derig_details || null)     : null,
    document_refs: src.document_refs || [],
  });

  // Backfill v1 from the ORIGINAL content if no versions exist yet, so the
  // first amendment produces a real v1 → v2 diff. Returns the highest version
  // number present afterwards.
  const ensureBaseline = async () => {
    const { count } = await supabase.from('brief_versions').select('id', { count: 'exact', head: true }).eq('brief_id', brief.id);
    if ((count || 0) === 0) {
      await supabase.from('brief_versions').insert([{ ...buildSnapshot(1, false, original), note_to_suppliers: null, created_at: brief.created_at || new Date().toISOString() }]);
      return 1;
    }
    const { data: latest } = await supabase.from('brief_versions').select('version_number').eq('brief_id', brief.id).order('version_number', { ascending: false }).limit(1);
    return latest?.[0]?.version_number || 1;
  };

  // INSERT first, then delete what it replaces — the same correction made to
  // the supplier response attachments. Delete-then-insert with neither call
  // checked means a failed insert leaves the brief with NO attachments at all,
  // on a brief that has already been sent to suppliers, and says nothing.
  // Returns an error for the caller to surface rather than swallowing it.
  const writeAttachments = async () => {
    const { data: prior } = await supabase
      .from('brief_attachments').select('id').eq('brief_id', brief.id);
    const priorIds = (prior || []).map(r => r.id);
    if (attachments.length > 0) {
      const { error: insErr } = await supabase.from('brief_attachments').insert(attachments.map(a => ({
        brief_id: brief.id, file_url: a.file_url, file_name: a.file_name,
        display_name: a.display_name && a.display_name.trim() !== '' ? a.display_name.trim() : null,
        file_size_bytes: a.file_size_bytes, mime_type: a.mime_type,
      })));
      // The old set is untouched, so the brief still has its documents.
      if (insErr) return insErr;
    }
    if (priorIds.length > 0) {
      // The other half of insert-then-delete. A failed delete leaves the OLD
      // documents sitting alongside the new ones, so a supplier opening the
      // brief sees both versions of every attachment with no way to tell which
      // is current. Not fatal — the new set is there — but it must be said.
      const { error: delErr } = await supabase.from('brief_attachments').delete().in('id', priorIds);
      if (delErr) return new Error(`The new documents saved, but the ones they replace couldn't be removed — suppliers will see both sets. (${delErr.message})`);
    }
    return null;
  };

  const save = async (silent) => {
    setSaving(true);
    setError(null);
    try {
      const baseVersion = await ensureBaseline();
      const nextVersion = baseVersion + 1;
      const deadlineChanged = blankNull(form.required_response_time) !== blankNull(original.required_response_time);

      // brief_type audit: stamp set_at + set_by whenever the type changes
      // (including → null when an operator removes a previously set type).
      // Unchanged saves don't touch the audit, so the original set-time
      // attribution survives across other-field amendments.
      const briefTypeChanged = (original.brief_type ?? null) !== (form.brief_type ?? null);
      const briefPatch = {
        brief_name: form.brief_name || null,
        fusion_project_number: form.fusion_project_number || null,
        fusion_project_name: form.fusion_project_name || null,
        brief_category: form.brief_category || null,
        client: form.client || null,
        required_response_time: blankNull(form.required_response_time),
        // Delivery: when "delivery required" is off (delivery_date_na=true)
        // the persisted date + address columns are blanked so the live
        // row matches the supplier's view.
        delivery_date_na: !!form.delivery_date_na,
        expected_required_by_date: form.delivery_date_na ? null : blankNull(form.expected_required_by_date),
        delivery_access_to_date: form.delivery_date_na ? null : blankNull(form.delivery_access_to_date),
        delivery_address: form.delivery_date_na ? null : (form.delivery_address || null),
        event_live_date: blankNull(form.event_live_date),
        event_live_end_date: blankNull(form.event_live_end_date),
        location: form.location || null,
        minimum_budget: blankNull(form.minimum_budget),
        maximum_budget: blankNull(form.maximum_budget),
        // Visibility flags travel with the values they control — otherwise
        // amending a field the supplier can't see leaves it invisible.
        show_client: !!form.show_client,
        show_event_date: !!form.show_event_date,
        show_delivery_date: !!form.show_delivery_date,
        show_budget: !!form.show_budget,
        show_location: !!form.show_location,
        show_project_name: !!form.show_project_name,
        show_project_number: !!form.show_project_number,
        show_category: !!form.show_category,
        show_delivery_address: !!form.show_delivery_address,
        show_rig: !!form.show_rig,
        show_derig: !!form.show_derig,
        brief_content: form.brief_content || null,
        brief_type: form.brief_type ?? null,
        nda_required: !!form.nda_required,
        // Rig / de-rig — same gate-on-required behaviour as the
        // creation form, so flipping the toggle off clears the
        // persisted columns rather than leaving orphan values.
        rig_required: !!form.rig_required,
        rig_start_date: form.rig_required ? blankNull(form.rig_start_date) : null,
        rig_end_date:   form.rig_required ? blankNull(form.rig_end_date)   : null,
        rig_details:    form.rig_required ? (form.rig_details || null)     : null,
        derig_required: !!form.derig_required,
        derig_start_date: form.derig_required ? blankNull(form.derig_start_date) : null,
        derig_end_date:   form.derig_required ? blankNull(form.derig_end_date)   : null,
        derig_details:    form.derig_required ? (form.derig_details || null)     : null,
        auto_reminder_24h: !!form.auto_reminder_24h,
        current_version: nextVersion,
        ...(briefTypeChanged
          ? {
              brief_type_set_at: form.brief_type ? new Date().toISOString() : null,
              brief_type_set_by: form.brief_type ? (currentInternalUser?.id || null) : null,
            }
          : {}),
      };
      // Material amendment with a moved deadline re-arms the 24h reminder.
      if (!silent && deadlineChanged) briefPatch.reminder_sent_at = null;
      // Toggling auto-reminder from off → on also re-arms it. Covers the
      // case where the cron already stamped reminder_sent_at (e.g. when
      // the brief originally had the toggle on, fired once, and the
      // operator now wants a fresh reminder after re-enabling).
      const autoReminderTurnedOn = !!form.auto_reminder_24h && !original.auto_reminder_24h;
      if (autoReminderTurnedOn) briefPatch.reminder_sent_at = null;

      const { error: upErr } = await supabase.from('briefs').update(briefPatch).eq('id', brief.id);
      if (upErr) { setError(`Could not save: ${upErr.message}`); setSaving(false); return; }

      // Cascade an extended brief deadline to per-recipient response_deadline
      // overrides. AddBrief stamps each recipient's response_deadline from
      // the brief's deadline at send-time, so without this cascade those
      // overrides are stuck at the OLD deadline — and the server-side
      // submission trigger uses COALESCE(rec_dl, brief_dl), preferring the
      // stale per-recipient value. We only bump rows whose deadline is
      // earlier than the new brief deadline, so deliberate per-supplier
      // extensions (later than the brief) survive.
      //
      // Checked, because the trigger PREFERS the per-recipient value: if this
      // cascade fails, the brief shows the new deadline while every supplier
      // is still held to the old one, and the amendment appears to have worked.
      const cascadeErrors = [];
      if (deadlineChanged && briefPatch.required_response_time) {
        const { error: cascadeErr } = await supabase.from('brief_recipients')
          .update({ response_deadline: briefPatch.required_response_time })
          .eq('brief_id', brief.id)
          .or(`response_deadline.is.null,response_deadline.lt.${briefPatch.required_response_time}`);
        if (cascadeErr) cascadeErrors.push(`suppliers are still on the old response deadline (${cascadeErr.message})`);
      }

      // Re-arm the 24h reminder for everyone on this brief when the deadline
      // moved or reminders were just enabled. The per-recipient reminder_sent_at
      // is the cron's fire-once guard now (the brief-level re-arm above is just
      // the informational marker), so clear it here too.
      if (!silent && (deadlineChanged || autoReminderTurnedOn)) {
        const { error: reArmErr } = await supabase.from('brief_recipients')
          .update({ reminder_sent_at: null })
          .eq('brief_id', brief.id);
        if (reArmErr) cascadeErrors.push(`the 24h reminder won't fire again for suppliers who already had one (${reArmErr.message})`);
      }

      const attErr = await writeAttachments();
      if (attErr) {
        setSaving(false);
        setError(`The brief saved, but its supporting documents didn't: ${attErr.message}. The previous documents are still attached.`);
        return;
      }
      // The version snapshot is what the supplier's "what changed" view diffs
      // against, so a silent failure here doesn't lose the amendment — it makes
      // the NEXT amendment diff against the wrong baseline.
      const { error: snapErr } = await supabase.from('brief_versions').insert([buildSnapshot(nextVersion, silent, currentSnap)]);
      if (snapErr) console.warn('Amend: version snapshot failed —', snapErr.message);

      let notified = 0;
      let reopened = 0;
      if (!silent) {
        // 2026-06 amend-flow:
        //
        //  1. Every actionable recipient (pending OR responded) flips
        //     back to 'pending' so the brief reappears under "Awaiting
        //     response" on the supplier portal. Declined / withdrawn
        //     stay untouched — those suppliers exited the conversation.
        //  2. amend_pending_since gets stamped so the portal banner
        //     ("X briefs amended — review the updates") can surface.
        //  3. last_seen_amend_version stays at whatever value it had —
        //     the gap to the new current_version is what drives the
        //     sticky CHANGED pills on the brief detail page.
        //
        // Checked, because this write is HOW A SUPPLIER FINDS OUT. It's what
        // moves the brief back under "Awaiting response", raises the portal
        // banner and drives the CHANGED pills. If it fails, the amendment
        // lands, the emails go out, and the portal shows no sign anything
        // changed — the worst combination, because the email points at a page
        // that contradicts it.
        const nowIso = new Date().toISOString();
        const { error: flipErr } = await supabase.from('brief_recipients')
          .update({ status: 'pending', amend_pending_since: nowIso })
          .eq('brief_id', brief.id)
          .in('status', ['pending', 'responded']);
        if (flipErr) cascadeErrors.push(`suppliers won't see the brief flagged as changed in the portal (${flipErr.message})`);

        // Reopen submitted responses for revision + reset edit_count so
        // each amend round gives the supplier a fresh window of 5 edits.
        const { data: reopenRows } = await supabase.from('brief_responses')
          .update({ amended_pending: true, amended_since: nowIso, edit_count: 0 })
          .eq('brief_id', brief.id).eq('is_submitted', true).select('id');
        reopened = (reopenRows || []).length;

        // Email non-declined recipients — unless the brief is muted.
        if (!brief.supplier_notifications_muted) {
          const deadlineDisplay = form.required_response_time ? fmtDate(form.required_response_time) : '';
          const briefUrl = `${window.location.origin}/portal`;
          for (const r of recipients) {
            if (r.status === 'declined' || r.suppliers?.is_record_only || r.suppliers?.status === 'inactive') continue;
            // Strict portal-user routing — type='portal_user' only. Legacy
            // rows are skipped so a Send Update never silently lands at
            // main_contact_email; the brief detail banner is the way to
            // sync legacy rows forward.
            const contacts = (Array.isArray(r.selected_contacts) ? r.selected_contacts : []).filter(c => c.selected && c.email && c.type === 'portal_user');
            if (contacts.length === 0) continue;
            const extras = r.additional_emails ? r.additional_emails.split(';').map(e => e.trim()).filter(Boolean) : [];
            const targets = [...contacts.map(c => ({ name: c.name, email: c.email })), ...extras.map(e => ({ name: '', email: e }))];
            let sentOne = false;
            for (const t of targets) {
              const { subject, html } = briefUpdatedEmail({ contactName: t.name, briefName: form.brief_name, deadline: deadlineDisplay, changeMessage: note.trim(), briefUrl });
              try { await sendEmail({ to: t.email, subject, html }); sentOne = true; } catch (e) { /* best-effort */ }
            }
            if (sentOne) {
              notified++;
              await supabase.from('brief_send_log').insert([{ brief_id: brief.id, recipient_id: r.id, supplier_id: r.supplier_id, kind: 'update', sent_by: currentInternalUser?.id || null }]);
            }
          }
        }
      }
      setSaving(false);
      // The amendment IS saved, so this isn't a failure — but the knock-on
      // writes that suppliers actually feel may not have landed, and the
      // caller's success toast would otherwise be the only thing on screen.
      if (cascadeErrors.length > 0) {
        setError(`The brief was amended, but: ${cascadeErrors.join('; ')}. Check the recipient list before relying on it.`);
        return;
      }
      onDone?.({ silent, notified, reopened });
    } catch (e) {
      setError(e.message || 'Amend failed');
      setSaving(false);
    }
  };

  const fieldCls = (label) => `bg-white border rounded-lg p-3 ${changedField(label) ? 'border-[#E89A2E]/50 bg-[#E89A2E]/[0.04]' : 'border-line'}`;
  const labelCls = 'font-[\'Metropolis\'] text-[10px] font-bold tracking-[0.18em] uppercase text-[#51534A] mb-1.5 flex items-center gap-2';
  const inputCls = "w-full bg-transparent border-0 text-ink font-['Metropolis'] text-sm outline-none p-0";
  const ChangedPill = () => <span className="bg-[#E89A2E]/15 text-[#7A4E10] rounded-full px-2 py-0.5 text-[8px] tracking-[0.14em] uppercase font-bold">Changed</span>;

  return (
    <div className="mb-8 bg-white border border-[#E89A2E]/30 rounded-xl p-6">
      <div className="flex items-center justify-between mb-5">
        <div className="flex items-center gap-3">
          <span className="font-['Metropolis'] text-sm font-extrabold tracking-[0.1em] uppercase text-ink">Amend Brief</span>
          <span className="inline-flex items-center gap-1.5 bg-[#E89A2E]/[0.12] text-[#7A4E10] border border-[#E89A2E]/30 rounded-full px-2.5 py-1 text-[9px] font-extrabold tracking-[0.14em] uppercase">
            <span className="w-1.5 h-1.5 rounded-full bg-current" /> Amending · unsaved changes
          </span>
        </div>
        <button type="button" onClick={onClose} className="font-['Metropolis'] text-xs font-bold tracking-[0.15em] uppercase text-[#51534A] hover:text-fusion-mid-grey transition-colors">Cancel</button>
      </div>

      {/* Editable fields — order MIRRORS AddBrief so operators see the
          same shape on create + amend. Field labels match across both
          surfaces too (e.g. "Brief Name", not "Title"). Sub-sections
          (Delivery, Rig, De-rig) follow AddBrief's collapsible-card
          pattern: a "required" toggle gates the detail fields and
          flipping it off clears the persisted columns on save. */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
        <div className={fieldCls('Project Number')}>
          <div className={labelCls}>Project Number {changedField('Project Number') && <ChangedPill />}</div>
          <input className={inputCls} value={form.fusion_project_number} onChange={e => set('fusion_project_number', e.target.value)} />
        </div>
        <div className={fieldCls('Client')}>
          <div className={labelCls}>Client {changedField('Client') && <ChangedPill />}</div>
          <input className={inputCls} value={form.client} onChange={e => set('client', e.target.value)} />
        </div>
        <div className={fieldCls('Project Name')}>
          <div className={labelCls}>Project Name {changedField('Project Name') && <ChangedPill />}</div>
          <input className={inputCls} value={form.fusion_project_name} onChange={e => set('fusion_project_name', e.target.value)} />
        </div>
        <div className={fieldCls('Brief Name')}>
          <div className={labelCls}>Brief Name {changedField('Brief Name') && <ChangedPill />}</div>
          <input className={inputCls} value={form.brief_name} onChange={e => set('brief_name', e.target.value)} />
        </div>
        <div className={fieldCls('Category')}>
          <div className={labelCls}>Category {changedField('Category') && <ChangedPill />}</div>
          <select className={`${inputCls} cursor-pointer`} value={form.brief_category} onChange={e => set('brief_category', e.target.value)}>
            <option value="">— Select —</option>
            {categories.map(c => <option key={c.value} value={c.value}>{c.value}</option>)}
          </select>
        </div>
        <div className={fieldCls('NDA Required')}>
          <div className={labelCls}>NDA Required {changedField('NDA Required') && <ChangedPill />}</div>
          <label className="inline-flex items-center gap-2.5 cursor-pointer">
            <input
              type="checkbox"
              checked={!!form.nda_required}
              onChange={e => set('nda_required', e.target.checked)}
              className="w-4 h-4 accent-active-green" />
            <span className="font-['Metropolis'] text-sm text-ink">{form.nda_required ? 'Yes — only suppliers with a valid NDA can be added' : 'No NDA required'}</span>
          </label>
        </div>

        {/* Brief Type — three-button toggle, full width. */}
        <div className={`${fieldCls('Brief Type')} md:col-span-2`}>
          <div className={labelCls}>Brief Type {changedField('Brief Type') && <ChangedPill />}</div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
            {[
              { value: null, label: "Don't indicate", accent: '#92D400' },
              { value: 'exclusive', label: 'Exclusive', accent: '#009159' },
              { value: 'competitive', label: 'Competitive', accent: '#2E8FA1' },
            ].map(opt => {
              const isSelected = (form.brief_type ?? null) === opt.value;
              return (
                <button
                  key={opt.value || 'unset'}
                  type="button"
                  onClick={() => set('brief_type', opt.value)}
                  className="font-['Metropolis'] text-xs font-semibold uppercase tracking-[0.1em] rounded-xl px-3 py-2 transition-colors"
                  style={{
                    background: isSelected ? `${opt.accent}22` : '#EEF1E6',
                    border: `1px solid ${isSelected ? opt.accent : '#DDE1D0'}`,
                    color: isSelected ? opt.accent : '#51534A',
                  }}
                >
                  {opt.label}
                </button>
              );
            })}
          </div>
        </div>

        <div className={`${fieldCls('Response Deadline')} md:col-span-2`}>
          <div className={labelCls}>Response Deadline <span className="text-[#51534A]/80 font-normal normal-case tracking-normal">({localTimezoneAbbr()})</span></div>
          {/* Locked here (2026-07): the deadline is changed with Extend Deadline
              (top of the page), which re-arms the 24h reminder and can target
              just the outstanding suppliers. Editing the brief-level deadline
              inline bypassed all of that, so it's read-only in Amend Brief. */}
          <div className="w-full bg-[#F2F4EA] border border-line rounded-lg px-3 py-2 text-sm text-fusion-mid-grey font-['Metropolis'] flex items-center justify-between gap-2">
            <span>{form.required_response_time ? fmtDateTime(form.required_response_time) : 'No deadline set'}</span>
            <span className="inline-flex items-center gap-1 text-[10px] font-bold text-[#51534A] uppercase tracking-[0.12em] flex-shrink-0">
              <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>
              Locked
            </span>
          </div>
          <p className="font-['Metropolis'] text-[11px] text-[#51534A] leading-snug mt-1.5">
            To move the deadline, use <strong className="text-ink">Extend Deadline</strong> at the top of this page — it can extend for everyone or just the suppliers still outstanding, and re-arms the 24-hour reminder.
          </p>
          {/* Auto-reminder opt-in — same 24h-too-late guard the
              creation form uses. */}
          {(() => {
            const MS_24H = 24 * 60 * 60 * 1000;
            const deadline = form.required_response_time ? new Date(form.required_response_time) : null;
            const msUntil = deadline ? deadline.getTime() - Date.now() : null;
            const tooSoon = msUntil != null && msUntil < MS_24H;
            const noDeadline = !deadline;
            const disabled = noDeadline || tooSoon;
            const MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
            const fireAt = deadline ? new Date(deadline.getTime() - MS_24H) : null;
            const fireAtLabel = fireAt
              ? `${fireAt.getDate()} ${MONTHS[fireAt.getMonth()]} ${fireAt.getFullYear()}, ${String(fireAt.getHours()).padStart(2,'0')}:${String(fireAt.getMinutes()).padStart(2,'0')}`
              : null;
            const alreadyFired = !!brief.reminder_sent_at;
            return (
              <div className="mt-3">
                <label className={`flex items-start gap-2.5 ${disabled ? 'cursor-not-allowed opacity-60' : 'cursor-pointer'}`}>
                  <input
                    type="checkbox"
                    checked={!!form.auto_reminder_24h && !disabled}
                    disabled={disabled}
                    onChange={e => set('auto_reminder_24h', e.target.checked)}
                    className="w-4 h-4 mt-0.5 accent-active-green flex-shrink-0 disabled:opacity-40" />
                  <div className="min-w-0">
                    <p className="font-['Metropolis'] text-xs font-semibold text-fusion-mid-grey">Send auto-reminder 24h before deadline</p>
                    <p className="font-['Metropolis'] text-[11px] text-[#51534A] leading-snug mt-0.5">
                      A single reminder email goes to any supplier who hasn't submitted yet, 24 hours before the deadline. Suppliers who've already submitted or declined are skipped.
                    </p>
                    {form.auto_reminder_24h && !disabled && fireAtLabel && (
                      <p className="font-['Metropolis'] text-[11px] text-fusion-forest mt-1">
                        Reminder will send: {fireAtLabel}
                      </p>
                    )}
                    {tooSoon && (
                      <p className="font-['Metropolis'] text-[11px] text-[#7A4E10] mt-1">
                        This deadline is less than 24 hours away, so a reminder can't be scheduled. Extend the deadline to enable reminders.
                      </p>
                    )}
                    {alreadyFired && !original.auto_reminder_24h && form.auto_reminder_24h && !disabled && (
                      <p className="font-['Metropolis'] text-[11px] text-[#51534A] mt-1 italic">
                        A reminder was previously sent on this brief — toggling back on will arm a fresh send at the new fire time.
                      </p>
                    )}
                  </div>
                </label>
              </div>
            );
          })()}
        </div>

        <div className={fieldCls('Event Live Start')}>
          <div className={labelCls}>Event Live Start {changedField('Event Live Start') && <ChangedPill />}</div>
          <DatePicker
            selected={toDateObj(form.event_live_date)}
            onChange={(d) => set('event_live_date', fromDateOnly(d))}
            dateFormat="dd/MM/yyyy"
            placeholderText="dd/mm/yyyy"
            isClearable
            className={inputCls}
            wrapperClassName="block w-full" />
        </div>
        <div className={fieldCls('Event Live End')}>
          <div className={labelCls}>Event Live End {changedField('Event Live End') && <ChangedPill />}</div>
          <DatePicker
            selected={toDateObj(form.event_live_end_date)}
            onChange={(d) => set('event_live_end_date', fromDateOnly(d))}
            dateFormat="dd/MM/yyyy"
            placeholderText="dd/mm/yyyy"
            isClearable
            minDate={toDateObj(form.event_live_date)}
            className={inputCls}
            wrapperClassName="block w-full" />
          {dateOrderErrors.event_live_end_date && (
            <p className="font-['Metropolis'] text-[11px] text-[#E0290A] mt-1">{dateOrderErrors.event_live_end_date}</p>
          )}
        </div>

        {/* Budget — Min + Max pair per 2026-06 budget-range migration.
            Two fieldCls wrappers so the amend-pill lights up
            independently when each side changes. */}
        <div className={fieldCls('Minimum Budget')}>
          <div className={labelCls}>Minimum Budget {changedField('Minimum Budget') && <ChangedPill />}</div>
          <MoneyInput value={form.minimum_budget} onChange={(v) => set('minimum_budget', v)} min="0" />
        </div>
        <div className={fieldCls('Maximum Budget')}>
          <div className={labelCls}>Maximum Budget {changedField('Maximum Budget') && <ChangedPill />}</div>
          <MoneyInput value={form.maximum_budget} onChange={(v) => set('maximum_budget', v)} min="0" />
        </div>
        <div className={fieldCls('Location')}>
          <div className={labelCls}>Location {changedField('Location') && <ChangedPill />}</div>
          <input className={inputCls} value={form.location} onChange={e => set('location', e.target.value)} />
        </div>

        {/* What the supplier sees. These toggles existed only on the CREATE
            form, which is the draft editor — so once a brief was sent this
            panel was the only way to edit it and had no way to reveal a field.
            Adding a budget to a live brief left it permanently hidden with no
            control anywhere to turn it on.
            A field with no value can still be ticked here: unlike the create
            form there's no "fill it in first" flow to fall back on, and the
            supplier panel already omits empty facts, so a tick simply means
            "show it when it has a value". */}
        <div className="md:col-span-2 bg-white border border-line rounded-xl p-4">
          <div className={labelCls}>What the supplier sees</div>
          <p className="font-['Metropolis'] text-[11px] text-[#51534A] mb-2.5">
            Tick a fact to show it on the supplier's view of this brief. Unticked facts stay internal.
          </p>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-4 gap-y-1.5">
            {[
              ['show_budget', 'Budget'],
              ['show_event_date', 'Event date'],
              ['show_delivery_date', 'Access dates'],
              ['show_delivery_address', 'Access address'],
              ['show_location', 'Location'],
              ['show_client', 'Client'],
              ['show_project_name', 'Project name'],
              ['show_project_number', 'Project number'],
              ['show_category', 'Category'],
              ['show_rig', 'Rig'],
              ['show_derig', 'De-rig'],
            ].map(([key, label]) => (
              <label key={key} className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={!!form[key]}
                  onChange={e => set(key, e.target.checked)}
                  className="w-4 h-4 accent-active-green flex-shrink-0" />
                <span className="font-['Metropolis'] text-[13px] text-fusion-mid-grey">{label}</span>
              </label>
            ))}
          </div>
        </div>

        {/* Delivery — required toggle gates the date + address fields,
            mirroring AddBrief. delivery_date_na is the inverse-of-
            required flag (kept that way to avoid a schema change). */}
        <div className={`md:col-span-2 bg-white border ${changedField('Access from') || changedField('Access to') || changedField('Access Address') ? 'border-[#E89A2E]/50 bg-[#E89A2E]/[0.03]' : 'border-line'} rounded-xl p-4`}>
          <label className="flex items-center gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={!form.delivery_date_na}
              onChange={e => {
                const required = e.target.checked;
                set('delivery_date_na', !required);
                if (!required) {
                  set('expected_required_by_date', '');
                  set('delivery_access_to_date', '');
                  set('delivery_address', '');
                }
              }}
              className="w-4 h-4 accent-active-green" />
            <div>
              <p className="font-['Metropolis'] text-sm text-fusion-mid-grey font-semibold">Access Required</p>
              <p className="font-['Metropolis'] text-xs text-[#51534A]">Tick if the supplier needs site access — opens the access dates and address fields.</p>
            </div>
          </label>
          {!form.delivery_date_na && (
            <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-3">
              <div className={fieldCls('Access from')}>
                <div className={labelCls}>Access from <span className="text-[#51534A]/80 font-normal normal-case tracking-normal">({localTimezoneAbbr()})</span> {changedField('Access from') && <ChangedPill />}</div>
                <DatePicker
                  selected={toDateObj(form.expected_required_by_date)}
                  onChange={(d) => set('expected_required_by_date', fromDateTime(d))}
                  showTimeSelect
                  timeFormat="HH:mm"
                  timeIntervals={15}
                  dateFormat="dd/MM/yyyy HH:mm"
                  placeholderText="dd/mm/yyyy --:--"
                  isClearable
                  className={inputCls}
                  wrapperClassName="block w-full" />
              </div>
              <div className={fieldCls('Access to')}>
                <div className={labelCls}>Access to <span className="text-[#51534A]/80 font-normal normal-case tracking-normal">(optional)</span> {changedField('Access to') && <ChangedPill />}</div>
                <DatePicker
                  selected={toDateObj(form.delivery_access_to_date)}
                  onChange={(d) => set('delivery_access_to_date', fromDateTime(d))}
                  showTimeSelect
                  timeFormat="HH:mm"
                  timeIntervals={15}
                  dateFormat="dd/MM/yyyy HH:mm"
                  placeholderText="dd/mm/yyyy --:--"
                  isClearable
                  className={inputCls}
                  wrapperClassName="block w-full" />
              </div>
              <div className={`md:col-span-2 ${fieldCls('Access Address')}`}>
                <div className={labelCls}>Access Address {changedField('Access Address') && <ChangedPill />}</div>
                <input className={inputCls} value={form.delivery_address} onChange={e => set('delivery_address', e.target.value)} placeholder="e.g. Site office, Silverstone Circuit" />
              </div>
            </div>
          )}
        </div>

        {/* Rig — required toggle gates dates + details. */}
        <div className={`md:col-span-2 bg-white border ${changedField('Install Required') || changedField('Install Start') || changedField('Install End') || changedField('Install Details') ? 'border-[#E89A2E]/50 bg-[#E89A2E]/[0.03]' : 'border-line'} rounded-xl p-4`}>
          <label className="flex items-center gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={!!form.rig_required}
              onChange={e => {
                const required = e.target.checked;
                set('rig_required', required);
                if (!required) {
                  set('rig_start_date', '');
                  set('rig_end_date', '');
                  set('rig_details', '');
                }
              }}
              className="w-4 h-4 accent-active-green" />
            <div>
              <p className="font-['Metropolis'] text-sm text-fusion-mid-grey font-semibold">Install Required {changedField('Install Required') && <ChangedPill />}</p>
              <p className="font-['Metropolis'] text-xs text-[#51534A]">Tick if there's an on-site rig phase — opens start/end + activities fields.</p>
            </div>
          </label>
          {form.rig_required && (
            <div className="mt-4 space-y-3">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div className={fieldCls('Install Start')}>
                  <div className={labelCls}>Install Start <span className="text-[#51534A]/80 font-normal normal-case tracking-normal">({localTimezoneAbbr()})</span> {changedField('Install Start') && <ChangedPill />}</div>
                  <DatePicker
                    selected={toDateObj(form.rig_start_date)}
                    onChange={(d) => set('rig_start_date', fromDateTime(d))}
                    showTimeSelect timeFormat="HH:mm" timeIntervals={15}
                    dateFormat="dd/MM/yyyy HH:mm" placeholderText="dd/mm/yyyy --:--"
                    isClearable className={inputCls} wrapperClassName="block w-full" />
                </div>
                <div className={fieldCls('Install End')}>
                  <div className={labelCls}>Install End <span className="text-[#51534A]/80 font-normal normal-case tracking-normal">({localTimezoneAbbr()})</span> {changedField('Install End') && <ChangedPill />}</div>
                  <DatePicker
                    selected={toDateObj(form.rig_end_date)}
                    onChange={(d) => set('rig_end_date', fromDateTime(d))}
                    showTimeSelect timeFormat="HH:mm" timeIntervals={15}
                    dateFormat="dd/MM/yyyy HH:mm" placeholderText="dd/mm/yyyy --:--"
                    isClearable minDate={toDateObj(form.rig_start_date)}
                    className={inputCls} wrapperClassName="block w-full" />
                  {dateOrderErrors.rig_end_date && (
                    <p className="font-['Metropolis'] text-[11px] text-[#E0290A] mt-1">{dateOrderErrors.rig_end_date}</p>
                  )}
                </div>
              </div>
              <div className={fieldCls('Install Details')}>
                <div className={labelCls}>Install Details {changedField('Install Details') && <ChangedPill />}</div>
                {/* Rich text — matches The Brief editor above; stored as HTML. */}
                <ReactQuill className="sp-quill" theme="snow" value={form.rig_details} onChange={(html) => set('rig_details', html)} placeholder="Activities, access requirements, crew expectations" />
              </div>
            </div>
          )}
        </div>

        {/* De-rig — required toggle gates dates + details. */}
        <div className={`md:col-span-2 bg-white border ${changedField('De-rig Required') || changedField('De-rig Start') || changedField('De-rig End') || changedField('De-rig Details') ? 'border-[#E89A2E]/50 bg-[#E89A2E]/[0.03]' : 'border-line'} rounded-xl p-4`}>
          <label className="flex items-center gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={!!form.derig_required}
              onChange={e => {
                const required = e.target.checked;
                set('derig_required', required);
                if (!required) {
                  set('derig_start_date', '');
                  set('derig_end_date', '');
                  set('derig_details', '');
                }
              }}
              className="w-4 h-4 accent-active-green" />
            <div>
              <p className="font-['Metropolis'] text-sm text-fusion-mid-grey font-semibold">De-rig Required {changedField('De-rig Required') && <ChangedPill />}</p>
              <p className="font-['Metropolis'] text-xs text-[#51534A]">Tick if there's a tear-down phase — opens start/end + activities fields.</p>
            </div>
          </label>
          {form.derig_required && (
            <div className="mt-4 space-y-3">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div className={fieldCls('De-rig Start')}>
                  <div className={labelCls}>De-rig Start <span className="text-[#51534A]/80 font-normal normal-case tracking-normal">({localTimezoneAbbr()})</span> {changedField('De-rig Start') && <ChangedPill />}</div>
                  <DatePicker
                    selected={toDateObj(form.derig_start_date)}
                    onChange={(d) => set('derig_start_date', fromDateTime(d))}
                    showTimeSelect timeFormat="HH:mm" timeIntervals={15}
                    dateFormat="dd/MM/yyyy HH:mm" placeholderText="dd/mm/yyyy --:--"
                    isClearable className={inputCls} wrapperClassName="block w-full" />
                </div>
                <div className={fieldCls('De-rig End')}>
                  <div className={labelCls}>De-rig End <span className="text-[#51534A]/80 font-normal normal-case tracking-normal">({localTimezoneAbbr()})</span> {changedField('De-rig End') && <ChangedPill />}</div>
                  <DatePicker
                    selected={toDateObj(form.derig_end_date)}
                    onChange={(d) => set('derig_end_date', fromDateTime(d))}
                    showTimeSelect timeFormat="HH:mm" timeIntervals={15}
                    dateFormat="dd/MM/yyyy HH:mm" placeholderText="dd/mm/yyyy --:--"
                    isClearable minDate={toDateObj(form.derig_start_date)}
                    className={inputCls} wrapperClassName="block w-full" />
                  {dateOrderErrors.derig_end_date && (
                    <p className="font-['Metropolis'] text-[11px] text-[#E0290A] mt-1">{dateOrderErrors.derig_end_date}</p>
                  )}
                </div>
              </div>
              <div className={fieldCls('De-rig Details')}>
                <div className={labelCls}>De-rig Details {changedField('De-rig Details') && <ChangedPill />}</div>
                {/* Rich text — sibling of Install Details; stored as HTML. */}
                <ReactQuill className="sp-quill" theme="snow" value={form.derig_details} onChange={(html) => set('derig_details', html)} placeholder="Get-out window, collection access, where kit lands after" />
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Body */}
      <div className={`${diff && diff.body.changed ? 'border-l-2 border-[#E89A2E] pl-3' : ''} mb-4`}>
        <div className={labelCls}>The Brief {diff && diff.body.changed && <ChangedPill />}</div>
        <ReactQuill className="sp-quill" theme="snow" value={form.brief_content} onChange={(html) => set('brief_content', html)} />
      </div>

      {/* Supporting documents */}
      <div className="mb-4">
        <div className={labelCls}>Supporting Documents</div>
        <div className="flex flex-wrap gap-2 mb-2">
          {attachments.map((a, i) => (
            <span key={i} className="inline-flex items-center gap-2 bg-white border border-line rounded-lg px-3 py-1.5 text-xs text-fusion-mid-grey font-['Metropolis']">
              {a.display_name || a.file_name}
              <button type="button" onClick={() => setAttachments(prev => prev.filter((_, x) => x !== i))} className="text-[#51534A] hover:text-[#E0290A] font-extrabold leading-none">×</button>
            </span>
          ))}
        </div>
        <label className="inline-flex items-center gap-2 cursor-pointer text-[#2E8FA1] text-xs font-bold font-['Metropolis']">
          <input type="file" multiple className="hidden" onChange={(e) => { handleFiles(e.target.files); e.target.value = ''; }} />
          {uploading ? 'Uploading…' : '+ Add files'}
        </label>
      </div>

      {/* Live change summary */}
      <div className="bg-[#E89A2E]/[0.06] border border-[#E89A2E]/30 rounded-xl p-4 mb-4">
        <div className="flex items-center gap-2 mb-2">
          <span className="font-['Metropolis'] text-[11px] font-extrabold tracking-[0.18em] uppercase text-[#7A4E10]">Changes to send</span>
          <span className="ml-auto font-['Metropolis'] text-[11px] text-fusion-mid-grey">{changed ? `${diff.fields.length + (diff.body.changed ? 1 : 0) + diff.documents.length} change${(diff.fields.length + (diff.body.changed ? 1 : 0) + diff.documents.length) === 1 ? '' : 's'}` : 'No changes yet'}</span>
        </div>
        {changed ? (
          <ul className="space-y-1 font-['Metropolis'] text-xs text-fusion-mid-grey">
            {diff.fields.map((f, i) => {
              // Rich-text fields (Install/De-rig Details) are stored as Quill
              // HTML; run every text value through htmlToPlainText so the diff
              // shows readable prose, not raw "<p>…&nbsp;…</p>". Harmless on the
              // plain-text fields (Location, Access Address).
              const fmt = f.type === 'currency' ? fmtMoney : f.type === 'datetime' ? fmtDateTime : f.type === 'date' ? fmtDate : f.type === 'enum' ? humaniseBriefType : f.type === 'bool' ? humaniseBool : (x) => (htmlToPlainText(x) || '—');
              return <li key={i}><strong className="text-ink">{f.field}</strong> · <span className="text-[#51534A] line-through">{fmt(f.was)}</span> <span className="text-[#7A4E10] font-bold">{fmt(f.now)}</span></li>;
            })}
            {diff.body.changed && <li><strong className="text-ink">The Brief</strong> · body updated</li>}
            {diff.documents.map((d, i) => <li key={`d${i}`}><strong className="text-ink">Documents</strong> · {d.tag === 'add' ? 'added' : 'removed'} {d.name}</li>)}
          </ul>
        ) : (
          <p className="font-['Metropolis'] text-xs text-[#51534A] italic">Edit a field above and the change summary appears here.</p>
        )}
      </div>

      {/* Note for suppliers */}
      <div className="mb-4">
        <div className={labelCls}>Note for suppliers (optional · included in the update email)</div>
        <AutoSizeTextarea value={note} maxLength={500} onChange={e => setNote(e.target.value)} minRows={2}
          placeholder="e.g. We've extended the deadline and slightly expanded scope. Please review and resubmit if anything's changed."
          className="w-full bg-white border border-line rounded-lg px-3 py-2 text-sm text-ink outline-none focus:border-[#92D400] font-['Metropolis']" />
      </div>

      {/* Recipient impact */}
      <div className="bg-white border border-line rounded-xl p-4 mb-4">
        <p className="font-['Metropolis'] text-[11px] font-extrabold tracking-[0.18em] uppercase text-fusion-forest mb-2">Who'll be notified</p>
        <ul className="space-y-1.5 font-['Metropolis'] text-xs">
          <li className="flex justify-between"><span className="text-ink"><span className="text-fusion-forest font-bold mr-1.5">{impact.submitted}</span>with submitted responses</span><span className="text-[#7A4E10] font-bold">Alert + reopen</span></li>
          <li className="flex justify-between"><span className="text-ink"><span className="text-fusion-forest font-bold mr-1.5">{impact.in_progress}</span>in progress</span><span className="text-fusion-mid-grey">Alert · draft preserved</span></li>
          <li className="flex justify-between"><span className="text-ink"><span className="text-fusion-forest font-bold mr-1.5">{impact.awaiting}</span>awaiting response</span><span className="text-fusion-mid-grey">Alert</span></li>
          <li className="flex justify-between"><span className="text-ink"><span className="text-fusion-forest font-bold mr-1.5">{impact.declined}</span>declined</span><span className="text-[#51534A] italic">Skipped</span></li>
        </ul>
        {brief.supplier_notifications_muted && <p className="font-['Metropolis'] text-[11px] text-[#7A4E10] mt-2">Notifications are muted for this brief — Send Update won't email anyone (the amendment still applies and responses still reopen).</p>}
      </div>

      {error && <p className="font-['Metropolis'] text-xs text-[#E0290A] mb-3">{error}</p>}

      {/* Footer — impact summary as a chip strip on the left (each
          consequence its own pill so the eye can scan rather than parse
          a run-on sentence), action buttons on the right with a single
          helper line beneath spanning the row so Save Silently's
          guidance doesn't push that one button taller than the other. */}
      <div className="pt-4 border-t border-line">
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-2 flex-wrap">
            {brief.supplier_notifications_muted ? (
              <span className="inline-flex items-center gap-1.5 font-['Metropolis'] text-[11px] font-bold rounded-full px-2.5 py-1 text-[#7A4E10] bg-[#E89A2E]/15 border border-[#E89A2E]/40">
                <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><line x1="1" y1="1" x2="23" y2="23"/><path d="M9 9v3a3 3 0 0 0 5.12 2.12M15 9.34V4a3 3 0 0 0-5.94-.6"/><path d="M17 16.95A7 7 0 0 1 5 12v-2m14 0v2a7 7 0 0 1-.11 1.23"/><line x1="12" y1="19" x2="12" y2="23"/></svg>
                Muted — no emails
              </span>
            ) : (
              <span className="inline-flex items-center gap-1.5 font-['Metropolis'] text-[11px] font-bold rounded-full px-2.5 py-1 text-fusion-forest bg-[#92D400]/15 border border-[#92D400]/40">
                <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/></svg>
                Email {notifyCount} supplier{notifyCount === 1 ? '' : 's'}
              </span>
            )}
            {impact.submitted > 0 && (
              <span className="inline-flex items-center gap-1.5 font-['Metropolis'] text-[11px] font-bold rounded-full px-2.5 py-1 text-[#7A4E10] bg-[#E89A2E]/15 border border-[#E89A2E]/40">
                <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><polyline points="1 4 1 10 7 10"/><path d="M3.51 15a9 9 0 1 0 2.13-9.36L1 10"/></svg>
                Reopen {impact.submitted} response{impact.submitted === 1 ? '' : 's'}
              </span>
            )}
            <span className="inline-flex items-center gap-1.5 font-['Metropolis'] text-[11px] font-bold rounded-full px-2.5 py-1 text-fusion-mid-grey bg-white border border-line">
              <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
              New version
            </span>
          </div>
          <div className="flex items-center gap-3 flex-shrink-0">
            <button type="button" onClick={() => save(true)} disabled={!changed || saving || !!dateOrderError} title={dateOrderError || undefined} className="inline-flex items-center gap-2 bg-transparent border border-line rounded-xl text-fusion-mid-grey font-['Metropolis'] text-sm font-bold px-5 py-2 hover:border-[#DADCCD] hover:text-ink transition-colors disabled:opacity-40 disabled:cursor-not-allowed">
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M13.73 21a2 2 0 0 1-3.46 0"/><path d="M18.63 13A17.89 17.89 0 0 1 18 8"/><path d="M6.26 6.26A5.86 5.86 0 0 0 6 8c0 7-3 9-3 9h14"/><path d="M18 8a6 6 0 0 0-9.33-5"/><line x1="1" y1="1" x2="23" y2="23"/></svg>
              {saving ? 'Saving…' : 'Save Silently'}
            </button>
            <button ref={sendUpdateBtnRef} type="button" onClick={() => setConfirmSend(true)} disabled={!changed || saving || !!dateOrderError} title={dateOrderError || undefined} className="bg-[#92D400] text-[#131313] rounded-xl font-['Metropolis'] text-sm font-extrabold px-5 py-2 hover:bg-[#a4e810] transition-colors disabled:opacity-40 disabled:cursor-not-allowed">
              Send Update →
            </button>
          </div>
        </div>
        {/* Helper line beneath the row — explains the silent path
            without forcing the Save Silently column to grow taller. */}
        <p className="font-['Metropolis'] text-[11px] text-fusion-mid-grey italic mt-3 leading-snug">
          Save Silently is for typos, formatting, or non-material fixes — no email, no response reopens, no supplier-side change markers.
        </p>
      </div>

      <ConfirmDialog
        open={confirmSend}
        title="Send this amendment?"
        body={brief.supplier_notifications_muted
          ? `This reopens ${impact.submitted} submitted response${impact.submitted === 1 ? '' : 's'} and creates a new brief version. Notifications are muted, so no emails go out.`
          : `This will email ${notifyCount} supplier${notifyCount === 1 ? '' : 's'}, reopen ${impact.submitted} submitted response${impact.submitted === 1 ? '' : 's'}, and create a new brief version.`}
        confirmLabel="Send Update"
        onConfirm={() => { setConfirmSend(false); onFlightFrom?.(sendUpdateBtnRef.current); save(false); }}
        onCancel={() => setConfirmSend(false)}
      />
    </div>
  );
}
